import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import { payBillingConversation } from '../../actions/conversations';
import { fetchPaymentMethods } from '../../actions/paymentMethods';
import { fetchUser as fetchUserAction } from '../../actions/user';
import { fetchTransferts as fetchTransfertsAction } from '../../actions/transferts';

import i18n from '../../i18n';

import Conversation from '../../models/Conversation';
import PaymentMethod from '../../models/PaymentMethod';
import Subscription from '../../models/Subscription';
import User from '../../models/User';
import PaymentModel from '../../models/Payment';

import { withCurrency } from '../currency';
import Loading from '../common/Loading';
import Analytics from '../../utils/analytics';
import { getSubscriptionType } from '../../utils/subscription';
import useGTM from '../analytics/useGTM';
import { fetchBillingAddress } from '../../actions/billingAddress';
import { fetchLegalEntityAssignment, fetchLegalEntityCountries } from '../../actions/legalEntity';

const Billing = React.lazy(() => import('../billing/Billing'));

export class Payment extends React.PureComponent {
    static propTypes = {
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        paymentMethods: PropTypes.arrayOf(PropTypes.instanceOf(PaymentMethod)),
        currency: PropTypes.string,
        user: PropTypes.instanceOf(User),
        newUserSubscription: PropTypes.instanceOf(Subscription),
        payBillingConversation: PropTypes.func.isRequired,
        fetchPaymentMethods: PropTypes.func.isRequired,
        fetchBillingAddress: PropTypes.func.isRequired,
        fetchLegalEntityAssignment: PropTypes.func.isRequired,
        fetchLegalEntityCountries: PropTypes.func.isRequired,
        fetchUser: PropTypes.func.isRequired,
        fetchTransferts: PropTypes.func.isRequired,
        coupon: PropTypes.shape({
            couponName: PropTypes.string
        }),
        is3xPaymentFromFinalization: PropTypes.bool, // for 3x payment
        updateIs3xPaymentFromFinalization: PropTypes.func,
        billingAddress: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.backToConversation = this.backToConversation.bind(this);
        this.payExchange = this.payExchange.bind(this);
        props.fetchPaymentMethods();
        props.fetchBillingAddress();
        props.fetchLegalEntityAssignment();
        props.fetchLegalEntityCountries();
    }

    backToConversation() {
        document.dispatchEvent(new CustomEvent('dismiss_card'));
    }

    payExchange(paymentData, paymentType, is3xPaymentFromFinalization = false, isPaymentMethod = false) {
        const { conversation, user, newUserSubscription, fetchUser, fetchTransferts, coupon } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();

        const boughtGp = conversation.hasToBuyGP();
        const boughtSubscription = !user.isSubscriber() && exchangeAsGuest.hasSelectedSubscription();

        return this.props
            .payBillingConversation(
                conversation.id,
                paymentData,
                paymentType,
                coupon.couponName,
                is3xPaymentFromFinalization,
                isPaymentMethod,
                user
            )
            .then(
                (response) => {
                    this.sendTracking(response, paymentType);

                    if (boughtSubscription || (boughtSubscription && boughtGp)) {
                        const params = new URLSearchParams({
                            source: 'at_exchange_guest',
                            type: getSubscriptionType(user, newUserSubscription),
                            redirectTo: `/conversations/${conversation.id}`,
                            conversationId: conversation.id
                        }).toString();
                        const confirmPath = user.isCollection()
                            ? '/collection/subscription/payment/confirm'
                            : '/subscription/payment/confirm';

                        document.location.assign(`${confirmPath}?${params}`);
                        return;
                    }

                    Promise.all([fetchTransferts(), fetchUser()]).then(() => {
                        this.backToConversation();
                        document.dispatchEvent(
                            new CustomEvent('open_finalize_modal', {
                                detail: { hasPurchase: boughtGp }
                            })
                        );
                    });
                },
                ({ payload }) => {
                    if (
                        payload &&
                        payload.error &&
                        payload.error.error &&
                        payload.error.error.details &&
                        payload.error.error.details.code
                    ) {
                        let title, text;
                        if (payload.error.error.details.code === 'host.already.have.finalized.exchanges') {
                            title = i18n.t('exchange:error.guest.finalized.exchange.title');
                            text = i18n.t('exchange:error.guest.finalized.exchange.description');
                        } else if (
                            payload.error.error.details.code === 'host.already.have.preapproved.exchanges'
                        ) {
                            title = i18n.t('exchange:error.guest.preapproved.exchange.title');
                            text = i18n.t('exchange:error.guest.preapproved.exchange.description');
                        }

                        if (title) {
                            sweetAlert({
                                title,
                                text,
                                icon: 'error'
                            });
                        }
                    }

                    const purchase = this.getPurchaseForTracking(paymentType);

                    this.sendPurchaseTracking({ ...purchase, payment_status: 'fail' });

                    return Promise.reject(payload);
                }
            );
    }

    sendTracking(response, paymentType) {
        const { conversation, user } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();

        let purchaseExchange = null;
        if (response && response.exchanges && response.exchanges.length > 0) {
            purchaseExchange = response.exchanges.find((e) => e.id === exchangeAsGuest.id);
        }

        const purchase = this.getPurchaseForTracking(paymentType);

        this.sendPurchaseTracking({
            ...purchase,
            order: purchaseExchange?.get('order'),
            payment_status: 'success'
        });

        Analytics.trackGTM('MixpanelSubscriptionPurchased', { userStatus: user.getStatus() });
    }

    sendPurchaseTracking(purchaseObject) {
        const { user } = this.props;
        const { fetchPurchase } = useGTM(user);

        Analytics.trackGTM('purchase', fetchPurchase(purchaseObject));
    }

    getPurchaseForTracking(paymentType) {
        const { conversation, currency, user, coupon, is3xPaymentFromFinalization } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        const total = exchangeAsGuest.getTotalToPay(false, user.isSubscriber());

        return {
            currency,
            price: total,
            coupon,
            type: paymentType === 'credit-card' || paymentType === 'saved-card' ? 'card' : 'paypal',
            is3xPayment: is3xPaymentFromFinalization,
            product: exchangeAsGuest.getEcommerceProducts(user.isSubscriber())
        };
    }

    render() {
        const { conversation, currency, paymentMethods, user, billingAddress } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        if (!conversation || !user || !currency || paymentMethods === null || billingAddress === undefined) {
            return <Loading />;
        }

        return (
            <div className="container">
                <Suspense fallback={<div>Loading...</div>}>
                    <Billing
                        backButton={{
                            text: i18n.t('subscription:back_to_conversation'),
                            action: this.backToConversation
                        }}
                        context="messaging"
                        currency={currency}
                        products={exchangeAsGuest.getProductsToPay(user.isSubscriber())}
                        paymentMethods={paymentMethods}
                        totalToPay={exchangeAsGuest.getTotalToPay(false, user.isSubscriber())}
                        payAction={this.payExchange}
                        is3xPaymentFromFinalization={this.props.is3xPaymentFromFinalization}
                        updateIs3xPaymentFromFinalization={this.props.updateIs3xPaymentFromFinalization}
                        needToBuyGp={conversation ? conversation.hasToBuyGP() : null}
                        source={PaymentModel.FINALIZATION_PAYMENT_SOURCE}
                        sourceId={conversation.id}
                    />
                </Suspense>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    paymentMethods: state.paymentMethods.data ? state.paymentMethods.data : [],
    user: state.user,
    newUserSubscription: state.subscription.subscription,
    coupon: state.subscription.coupon,
    billingAddress: state.billingAddress.billingAddress
});

const mapDispatchToProps = (dispatch) => ({
    payBillingConversation: bindActionCreators(payBillingConversation, dispatch),
    fetchTransferts: bindActionCreators(fetchTransfertsAction, dispatch),
    fetchUser: bindActionCreators(fetchUserAction, dispatch),
    fetchPaymentMethods: bindActionCreators(fetchPaymentMethods, dispatch),
    fetchBillingAddress: bindActionCreators(fetchBillingAddress, dispatch),
    fetchLegalEntityAssignment: bindActionCreators(fetchLegalEntityAssignment, dispatch),
    fetchLegalEntityCountries: bindActionCreators(fetchLegalEntityCountries, dispatch)
});

export default compose(withCurrency, connect(mapStateToProps, mapDispatchToProps))(Payment);
